import { Discount, FiscalDocument, FiscalDocumentStatus, getActiveInvoices, isUninvoicedFiscalDocumentStatus } from '../../../../fiscal-documents'
import { round } from '../../../../../utils/typeUtils'
import { AdjustmentNote } from '../../../state/types'
import { isManualCreditNote } from '../../../utils/manualCreditNoteUtils'

export const PRICE_ADJUSTMENT_NAME = 'Ajuste'

// Nombre que llevaban los ítems negativos de las facturas emitidas antes del descuento estructurado
const LEGACY_PRICE_ADJUSTMENT_NAME = 'Bonificación / Ajuste de precio'

// Distingue el descuento de un ajuste de precio de cualquier otro descuento del comprobante
export const PRICE_ADJUSTMENT_DISCOUNT_KIND = 'price-adjustment'

const AMOUNT_TOLERANCE = 0.01

// NC manual de ajuste de precio (no de cancelaciones ni devoluciones).
// Las notas anteriores a la marca `manual` se reconocen por el nombre del ítem.
export const isManualPriceAdjustment = isManualCreditNote

// Ajuste de precio sobre lo que todavía no se facturó: nunca se envió a ARCA, así que lo que queda por facturar
// ya viene rebajado por él. Las notas por cancelaciones o devoluciones no entran: sus cantidades ya salen de
// los ítems a facturar.
export const isInternalPriceAdjustment = (adjustmentNote: AdjustmentNote): boolean =>
    isManualPriceAdjustment(adjustmentNote) && adjustmentNote.fiscalStatus === FiscalDocumentStatus.NOT_APPLICABLE

// La NC fiscal de un ajuste de precio rebaja lo facturado pero no devuelve nada para volver a facturar
// (a diferencia de la de una devolución): se excluye al calcular el valor facturado de un acopio financiero
export const withoutPriceAdjustmentCreditNotes = (
    fiscalDocuments: FiscalDocument[],
    adjustmentNotes: AdjustmentNote[]
): FiscalDocument[] => {
    const manualNoteIds = new Set(adjustmentNotes.filter(isManualPriceAdjustment).map(adjustmentNote => adjustmentNote.id))
    return fiscalDocuments.filter(fiscalDocument => !fiscalDocument.adjustmentNoteId || !manualNoteIds.has(fiscalDocument.adjustmentNoteId))
}

// Bonificación ya descontada por una factura: el descuento estructurado o, en facturas emitidas antes de él,
// los ítems negativos que llevaba la bonificación.
const getAbsorbedByInvoice = (invoice: FiscalDocument): number => {
    const discounts = (invoice.details?.discounts || [])
        .filter(discount => discount.kind === PRICE_ADJUSTMENT_DISCOUNT_KIND)
        .reduce((sum, discount) => sum + discount.amount, 0)
    // Compatibilidad de lectura con facturas emitidas con líneas negativas
    const legacyItems = (invoice.details?.items || [])
        .filter(item => item.name.startsWith(LEGACY_PRICE_ADJUSTMENT_NAME))
        .reduce((sum, item) => sum - (item.totalWithoutVAT + item.vatAmount), 0)
    return discounts + legacyItems
}

// Bonificación ya descontada por las facturas activas (enviadas o autorizadas y no anuladas).
// Una factura rechazada o anulada deja de contar, así que su bonificación vuelve a quedar pendiente.
export const getAbsorbedPriceAdjustment = (fiscalDocuments: FiscalDocument[]): number =>
    round(getActiveInvoices(fiscalDocuments)
        .filter(invoice => !isUninvoicedFiscalDocumentStatus(invoice.status))
        .reduce((sum, invoice) => sum + getAbsorbedByInvoice(invoice), 0))

// Ajuste interno que todavía no descontó ninguna factura. El monto de la nota ya está en el
// espacio del comprobante (con recargo del medio de pago, sin percepciones).
export const getPendingPriceAdjustment = (
    adjustmentNotes: AdjustmentNote[],
    fiscalDocuments: FiscalDocument[]
): number => round(adjustmentNotes
    .filter(isInternalPriceAdjustment)
    .reduce((sum, adjustmentNote) => sum + adjustmentNote.amount, 0) - getAbsorbedPriceAdjustment(fiscalDocuments))

export type PriceAdjustment = {
    // Descuentos a enviar en el comprobante (uno solo con el total del ajuste pendiente)
    discounts: Discount[]
    // Monto total del descuento, siempre positivo
    amount: number
}

const NO_PRICE_ADJUSTMENT: PriceAdjustment = { discounts: [], amount: 0 }

// Lo que queda por facturar es lo que falta facturar a precio lleno menos el ajuste pendiente.
// Mientras la factura entre en eso sale sin bonificación; si lo supera, lleva un descuento por la
// diferencia. El descuento se topa en el monto del comprobante: si el ajuste cubre todo el saldo
// el total quedaría en cero, y ese comprobante no se ofrece (no se puede emitir).
export const buildPriceAdjustment = (
    adjustmentNotes: AdjustmentNote[],
    fiscalDocuments: FiscalDocument[],
    documentAmount: number,
    remainingFullAmount: number
): PriceAdjustment => {
    const pendingAdjustment = getPendingPriceAdjustment(adjustmentNotes, fiscalDocuments)
    if (pendingAdjustment <= AMOUNT_TOLERANCE) return NO_PRICE_ADJUSTMENT

    const remainingAmount = Math.max(0, round(remainingFullAmount - pendingAdjustment))
    const amount = round(Math.min(pendingAdjustment, Math.max(0, documentAmount - remainingAmount)))
    if (amount <= AMOUNT_TOLERANCE) return NO_PRICE_ADJUSTMENT

    return {
        discounts: [{ reason: PRICE_ADJUSTMENT_NAME, amount, kind: PRICE_ADJUSTMENT_DISCOUNT_KIND }],
        amount
    }
}

// El ajuste cubre todo el comprobante: no hay nada para facturar y el backend rechaza un total en cero
export const isCoveredByPriceAdjustment = (documentAmount: number, priceAdjustment: PriceAdjustment): boolean =>
    priceAdjustment.amount > AMOUNT_TOLERANCE &&
    documentAmount - priceAdjustment.amount <= AMOUNT_TOLERANCE

export const getPreviewItemsTotal = (items: { totalWithoutVAT: number, vatAmount: number }[]): number =>
    round(items.reduce((sum, item) => sum + item.totalWithoutVAT + item.vatAmount, 0))
